
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
  Badge,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  Droplet,
  Trash2,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import { fetchWardsThunk } from "../../../store/slices/wardsSlice";
import { fetchMeterTypesThunk } from "../../../store/slices/meterTypesSlice";
import { fetchSlrmTypesThunk } from "../../../store/slices/slrmTypesSlice";

import {
  fetchZeroBillReportThunk,
  clearZeroBillReport,
} from "../../../store/slices/zeroBillReportSlice";


import { printTableReport } from "./reportPrintUtils";

import "./ReportPage.css";

const ZeroBillReportPage = () => {
  const dispatch = useDispatch();

  const wards = useSelector((state) => state.wards?.list || []);

  const meterTypes = useSelector(
    (state) => state.meterTypes?.list || [],
  );

  const slrmTypes = useSelector(
    (state) => state.slrmTypes?.list || [],
  );

  const {
    rows = [],
    loading = false,
    error = null,
  } = useSelector((state) => state.zeroBillReport || {});

  const [billType, setBillType] = useState("water");
  const [wardId, setWardId] = useState("");
  const [mtrType, setMtrType] = useState("");
  const [slrmType, setSlrmType] = useState("");
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    dispatch(fetchWardsThunk());
    dispatch(fetchMeterTypesThunk());
    dispatch(fetchSlrmTypesThunk());
  }, [dispatch]);

  useEffect(() => {
    setPage(1);

    if (billType === "water") {
      setSlrmType("");
    } else {
      setMtrType("");
    }
  }, [billType]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc",
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };

  const filteredRows = useMemo(() => {
    const reportRows = Array.isArray(rows) ? [...rows] : [];
    const query = search.trim().toLowerCase();

    const searchedRows = !query
      ? reportRows
      : reportRows.filter((row) => {
          const searchableText = [
            row.home_id,
            row.qr_code,
            row.door_no,
            row.family_head,
            row.mobile,
            row.ward_name,
            row.water_type_name,
            row.slrm_type_name,
            row.is_water,
            row.is_slrm,
            row.bill_type,
            row.bill_count,
            row.status,
          ]
            .map((value) => String(value ?? ""))
            .join(" ")
            .toLowerCase();

          return searchableText.includes(query);
        });

    if (!sortField) {
      return searchedRows;
    }

    const numericFields = [
      "home_id",
      "mobile",
      "bill_count",
    ];

    return [...searchedRows].sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];

      if (numericFields.includes(sortField)) {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);

        return sortDirection === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      const comparison = String(aValue || "").localeCompare(
        String(bValue || ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        },
      );

      return sortDirection === "asc"
        ? comparison
        : -comparison;
    });
  }, [rows, search, sortField, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [
    billType,
    wardId,
    mtrType,
    slrmType,
    search,
    rows.length,
    pageSize,
  ]);

  const total = filteredRows.length;

  const totalPages = Math.max(
    1,
    Math.ceil(total / pageSize),
  );

  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;

    return filteredRows.slice(
      startIndex,
      startIndex + pageSize,
    );
  }, [filteredRows, currentPage, pageSize]);

  const handleLoad = () => {
    dispatch(
      fetchZeroBillReportThunk({
        bill_type: billType,
        ward_id: wardId,
        mtr_type: billType === "water" ? mtrType : "",
        slrm_type: billType === "slrm" ? slrmType : "",
      }),
    );
  };

  const handleClear = () => {
    setBillType("water");
    setWardId("");
    setMtrType("");
    setSlrmType("");
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);

    dispatch(clearZeroBillReport());
  };

  const getBillTypeLabel = () => {
    return billType === "water" ? "Water" : "SLRM";
  };

  const displayValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "-";
    }

    return value;
  };

  const exportExcel = () => {
    const data = filteredRows.map((row, index) => ({
      "Sl No.": index + 1,
      "QR Code": row.qr_code ?? "",
      "Door No": row.door_no ?? "",
      "Family Head": row.family_head ?? "",
      Mobile: row.mobile ?? "",
      Ward: row.ward_name ?? "",
      "Water Rate": row.water_type_name ?? "",
      "SLRM Rate": row.slrm_type_name ?? "",
      Water: row.is_water ?? "",
      SLRM: row.is_slrm ?? "",
      "Bill Type": row.bill_type ?? "",
      "Bill Count": row.bill_count ?? 0,
      Status: row.status ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 16 },
      { wch: 45 },
      { wch: 16 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Zero Bill Report",
    );

    XLSX.writeFile(
      workbook,
      `zero_${billType}_bill_report.xlsx`,
    );
  };

  const exportPDF = () => {
    const data = filteredRows.map((row, index) => ({
      "Sl No.": index + 1,
      "QR Code": row.qr_code ?? "",
      "Door No": row.door_no ?? "",
      "Family Head": row.family_head ?? "",
      Mobile: row.mobile ?? "",
      Ward: row.ward_name ?? "",
      "Water Rate": row.water_type_name ?? "",
      "SLRM Rate": row.slrm_type_name ?? "",
      Water: row.is_water ?? "",
      SLRM: row.is_slrm ?? "",
      "Bill Type": row.bill_type ?? "",
      "Bill Count": row.bill_count ?? 0,
      Status: row.status ?? "",
    }));

    printTableReport({
      title: `Zero ${getBillTypeLabel()} Bill - Report`,
fileName: "zero_bill_report.pdf",
      data,
      metaLines: [`Total Records: ${filteredRows.length}`],
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    const items = [];
    const maxButtons = 5;

    let start = Math.max(
      1,
      currentPage - Math.floor(maxButtons / 2),
    );

    let end = Math.min(
      totalPages,
      start + maxButtons - 1,
    );

    start = Math.max(1, end - maxButtons + 1);

    items.push(
      <Pagination.Prev
        key="previous"
        disabled={currentPage === 1}
        onClick={() =>
          setPage((previous) =>
            Math.max(1, previous - 1),
          )
        }
      />,
    );

    if (start > 1) {
      items.push(
        <Pagination.Item
          key={1}
          onClick={() => setPage(1)}
        >
          1
        </Pagination.Item>,
      );

      if (start > 2) {
        items.push(
          <Pagination.Ellipsis
            key="start-ellipsis"
            disabled
          />,
        );
      }
    }

    for (
      let pageNumber = start;
      pageNumber <= end;
      pageNumber += 1
    ) {
      items.push(
        <Pagination.Item
          key={pageNumber}
          active={pageNumber === currentPage}
          onClick={() => setPage(pageNumber)}
        >
          {pageNumber}
        </Pagination.Item>,
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(
          <Pagination.Ellipsis
            key="end-ellipsis"
            disabled
          />,
        );
      }

      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() =>
          setPage((previous) =>
            Math.min(totalPages, previous + 1),
          )
        }
      />,
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                Zero Bill - Report
              </div>

              <div className="text-muted small mt-1">
                View houses without generated Water or SLRM
                bills
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          <div className="report-filter-box">
            <Row className="g-3">
              <Col xl={3} lg={4} md={6}>
                <Form.Label className="report-label">
                  Bill Type
                </Form.Label>

                <Form.Select
                  value={billType}
                  onChange={(event) =>
                    setBillType(event.target.value)
                  }
                >
                  <option value="water">
                    Water Bill
                  </option>

                  <option value="slrm">
                    SLRM Bill
                  </option>
                </Form.Select>
              </Col>

              <Col xl={3} lg={4} md={6}>
                <Form.Label className="report-label">
                  Ward
                </Form.Label>

                <Form.Select
                  value={wardId}
                  onChange={(event) =>
                    setWardId(event.target.value)
                  }
                >
                  <option value="">All Wards</option>

                  {wards.map((ward) => (
                    <option
                      key={ward.ward_id}
                      value={String(ward.ward_id)}
                    >
                      {ward.ward_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {billType === "water" ? (
                <Col xl={3} lg={4} md={6}>
                  <Form.Label className="report-label">
                    Water Rate
                  </Form.Label>

                  <Form.Select
                    value={mtrType}
                    onChange={(event) =>
                      setMtrType(event.target.value)
                    }
                  >
                    <option value="">
                      All Water Rates
                    </option>

                    {meterTypes.map((meterType) => (
                      <option
                        key={meterType.wbs_id}
                        value={String(
                          meterType.wbs_id,
                        )}
                      >
                        {meterType.mtr_type}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              ) : (
                <Col xl={3} lg={4} md={6}>
                  <Form.Label className="report-label">
                    SLRM Rate
                  </Form.Label>

                  <Form.Select
                    value={slrmType}
                    onChange={(event) =>
                      setSlrmType(event.target.value)
                    }
                  >
                    <option value="">
                      All SLRM Rates
                    </option>

                    {slrmTypes.map((slrmRate) => (
                      <option
                        key={slrmRate.wbs_id}
                        value={String(
                          slrmRate.wbs_id,
                        )}
                      >
                        {slrmRate.mtr_type}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              )}

              <Col
                xl={3}
                lg={12}
                md={6}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={handleLoad}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={handleClear}
                    disabled={loading}
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              <span className="d-inline-flex align-items-center gap-2">
                {billType === "water" ? (
                  <Droplet size={18} />
                ) : (
                  <Trash2 size={18} />
                )}

                Zero {getBillTypeLabel()} Bills
              </span>

              <span className="report-count ms-2">
                ({total.toLocaleString()} records)
              </span>
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search house / name / ward / mobile..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={!filteredRows.length}
              >
                <Download
                  size={16}
                  className="me-2"
                />
                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={!filteredRows.length}
              >
                <Download
                  size={16}
                  className="me-2"
                />
                PDF
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll zero-bill-table-scroll">
              <Table
                hover
                className="mb-0 report-table zero-bill-table"
              >
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "home_id"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("home_id")
                        }
                      >
                        Sl No.
                        {renderSortIcon("home_id")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "qr_code"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("qr_code")
                        }
                      >
                        QR Code
                        {renderSortIcon("qr_code")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "door_no"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("door_no")
                        }
                      >
                        Door No
                        {renderSortIcon("door_no")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "family_head"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("family_head")
                        }
                      >
                        Family Head
                        {renderSortIcon("family_head")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "mobile"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("mobile")
                        }
                      >
                        Mobile
                        {renderSortIcon("mobile")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "ward_name"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("ward_name")
                        }
                      >
                        Ward
                        {renderSortIcon("ward_name")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "water_type_name"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("water_type_name")
                        }
                      >
                        Water Rate
                        {renderSortIcon("water_type_name")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "slrm_type_name"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("slrm_type_name")
                        }
                      >
                        SLRM Rate
                        {renderSortIcon("slrm_type_name")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "is_water"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("is_water")
                        }
                      >
                        Water
                        {renderSortIcon("is_water")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "is_slrm"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("is_slrm")
                        }
                      >
                        SLRM
                        {renderSortIcon("is_slrm")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "bill_type"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("bill_type")
                        }
                      >
                        Bill Type
                        {renderSortIcon("bill_type")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "bill_count"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("bill_count")
                        }
                      >
                        Bill Count
                        {renderSortIcon("bill_count")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "status"
                            ? "is-active"
                            : ""
                        }`}
                        onClick={() =>
                          handleSort("status")
                        }
                      >
                        Status
                        {renderSortIcon("status")}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={13}
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map((row, index) => (
                      <tr
                        key={`${row.bill_type}-${row.home_id}`}
                      >
                        <td>
                          {(currentPage - 1) *
                            pageSize +
                            index +
                            1}
                        </td>

                        <td>
                          {displayValue(row.qr_code)}
                        </td>

                        <td>
                          {displayValue(row.door_no)}
                        </td>

                        <td className="zero-bill-family-head">
                          {displayValue(row.family_head)}
                        </td>

                        <td>
                          {displayValue(row.mobile)}
                        </td>

                        <td>
                          {displayValue(row.ward_name)}
                        </td>

                        <td>
                          {displayValue(
                            row.water_type_name,
                          )}
                        </td>

                        <td>
                          {displayValue(
                            row.slrm_type_name,
                          )}
                        </td>

                        <td>
                          <Badge
                            bg="light"
                            text="dark"
                            className="border text-capitalize"
                          >
                            {displayValue(row.is_water)}
                          </Badge>
                        </td>

                        <td>
                          <Badge
                            bg="light"
                            text="dark"
                            className="border text-capitalize"
                          >
                            {displayValue(row.is_slrm)}
                          </Badge>
                        </td>

                        <td>
                          <Badge
                            bg="light"
                            text="dark"
                            className="border text-uppercase"
                          >
                            {displayValue(row.bill_type)}
                          </Badge>
                        </td>

                        <td>
                          <Badge
                            bg="light"
                            text="dark"
                            className="border"
                          >
                            {row.bill_count ?? 0}
                          </Badge>
                        </td>

                        <td>
                          <Badge
                            bg="light"
                            text="dark"
                            className="border"
                          >
                            {displayValue(row.status)}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={13}
                        className="text-center py-5"
                      >
                        <div className="fw-semibold">
                          No zero-bill houses found
                        </div>

                        <div className="text-muted small mt-1">
                          Select filters and click Load.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>

                <Form.Select
                  value={pageSize}
                  onChange={(event) =>
                    setPageSize(
                      Number(event.target.value),
                    )
                  }
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map((size) => (
                    <option
                      key={size}
                      value={size}
                    >
                      {size}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">
                Page {currentPage} / {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ZeroBillReportPage;
